/*
  Расхождения с портфелем - макет А «Одной таблицей», выбранный владельцем
  01.10.2026 (строка 178 реестра компромиссов).

  Все расхождения одним списком с флажками: сначала по существу (другой дом,
  квартира, вид), ниже - тот же дом и квартира, записанные иначе. Порядок задаёт
  сервер. В графе «Что изменится» - последствие замены словами сервера: дом и
  участок, к кому иск, поданные документы. Две кнопки внизу действуют на
  отмеченное; ошибка - под ними.

  Отметки и кнопки действуют только на ПОКАЗАННЫЕ строки (01.10.2026, проверка
  перед выкладкой): флажок в шапке называется «отметить показанные», а число в
  кнопках - отмеченные из видимых. Строку, которой человек не видел, замена не
  тронет; чтобы решить по всем сразу, их показывают все («Показать все»).

  На узком экране шапка таблицы спрятана вместе с её флажком, поэтому над
  таблицей стоит видимая кнопка «Отметить показанные», а спрятанный флажок
  убран из обхода клавиатурой (`styles.adaptive.css`, `.divergence__all`).

  Пока идёт перенос, кнопки выключены: сервер всё равно отказал бы (409).
*/

import { Link } from 'react-router-dom'

import { DIFFERENCE_TITLES, FACT_TITLES } from '../../api/format.js'
import { Lock } from '../AccessContext.jsx'
import { Check, Notice, Tag } from '../Common.jsx'
import changedParts from './changedParts.js'
import TransferLock from './TransferLock.jsx'
import useDivergences, { keyOf, PAGE } from './useDivergences.js'

// Подписи сведений во вкладке строчные («адрес»), в графе таблицы - с заглавной.
const capitalised = (text) => text.charAt(0).toUpperCase() + text.slice(1)

const DIFFERENCE_TAG = {
  place: 'warn',
  ownership: 'warn',
  fuller: 'muted',
  written: 'muted',
  shorter: 'burn',
}

export default function Divergences({ revision, onDecided, locked = false }) {
  const { items, visible, chosen, showMore, toggle, chooseShown, decide, busy, error, loadError } =
    useDivergences(revision, onDecided)

  if (loadError) return <Notice error={loadError} />
  if (!items.length) return null

  const allChosen = chosen.length === visible.length
  const shownTitle = `Отметить показанные расхождения (${visible.length})`
  const hidden = items.length - visible.length

  return (
    <section className="section">
      <h2 className="section__title">Расхождения с портфелем · {items.length}</h2>
      <p className="muted">
        Присланные файлы называют по счетам портфеля не то, что в нём записано. Ничего не меняется
        само: отметьте, что заменить, а что оставить.
      </p>
      <div className="card">
        <div className="row divergence__all">
          <button type="button" className="button button--quiet" onClick={() => chooseShown(!allChosen)}>
            {allChosen ? `Снять отметки с показанных (${visible.length})` : shownTitle}
          </button>
        </div>
        <div className="table-scroll">
          <table className="table table--fixed table--divergences">
            <thead>
              <tr>
                <th className="table__control">
                  <Check
                    checked={allChosen}
                    indeterminate={chosen.length > 0 && !allChosen}
                    onChange={chooseShown}
                    title={shownTitle}
                  />
                </th>
                <th>Счёт</th>
                <th>Что</th>
                <th>В портфеле</th>
                <th>В файле</th>
                <th>Что изменится</th>
              </tr>
            </thead>
            <tbody>
              {visible.map((item) => (
                <DivergenceRow
                  key={keyOf(item)}
                  item={item}
                  checked={chosen.includes(keyOf(item))}
                  onToggle={() => toggle(keyOf(item))}
                />
              ))}
            </tbody>
          </table>
        </div>
        {hidden > 0 ? (
          <div className="table-more">
            <span className="muted">
              Показано {visible.length} из {items.length}. Непоказанные строки кнопки ниже не
              трогают.
            </span>
            <div className="row">
              <button type="button" className="button button--quiet" onClick={() => showMore(PAGE)}>
                Показать ещё {Math.min(PAGE, hidden)}
              </button>
              {hidden > PAGE ? (
                <button type="button" className="button button--quiet" onClick={() => showMore(hidden)}>
                  Показать все ({items.length})
                </button>
              ) : null}
            </div>
          </div>
        ) : null}
      </div>
      <p className="muted">
        Отмечено {chosen.length} из показанных {visible.length}. Заменённое пишется в карточку счёта
        и в журнал действий с именем файла; после смены вида собственности состав должников счёта
        уходит на проверку.
      </p>
      {/* Решение - запись: при неоплаченной лицензии кнопки гаснут (строка 291 реестра). */}
      <Lock>
        <div className="row">
          <button
            type="button"
            className="button"
            disabled={busy || locked || chosen.length === 0}
            onClick={() => decide(true)}
          >
            Заменить на значение из файла ({chosen.length})
          </button>
          <button
            type="button"
            className="button button--quiet"
            disabled={busy || locked || chosen.length === 0}
            onClick={() => decide(false)}
          >
            Оставить как в портфеле ({chosen.length})
          </button>
        </div>
      </Lock>
      <TransferLock locked={locked} />
      <Notice error={error} />
    </section>
  )
}

function DivergenceRow({ item, checked, onToggle }) {
  return (
    <tr>
      <td className="table__control">
        <Check checked={checked} onChange={onToggle} title={`Отметить счёт ${item.account_number}`} />
      </td>
      <td>
        <Link to={`/accounts/${item.account_id}`}>№ {item.account_number}</Link>
      </td>
      {/*
        Содержимое ячейки - одним блоком: на узком экране ячейка становится
        строкой карточки, и два куска в ней разъезжались по краям (осмотр на
        768, 01.10.2026).
      */}
      <td data-label="Что">
        <div>
          <div>{capitalised(FACT_TITLES[item.fact] ?? item.fact)}</div>
          <Tag kind={DIFFERENCE_TAG[item.difference] ?? 'muted'}>
            {DIFFERENCE_TITLES[item.difference] ?? item.difference}
          </Tag>
        </div>
      </td>
      <td data-label="В портфеле" className="muted">
        {item.portfolio_value}
      </td>
      <td data-label="В файле" className="table__block">
        <div>
          <div className="divergence__new">
            {changedParts(item.portfolio_value, item.file_value).map((part, index) =>
              // Ключ - порядковый номер: куски одной строки только на чтение,
              // заменяются целиком и своего идентификатора не имеют.
              part.changed ? <mark key={index}>{part.text}</mark> : <span key={index}>{part.text}</span>,
            )}
          </div>
          <div className="muted filename">{item.filenames.map((name) => `«${name}»`).join(', ')}</div>
        </div>
      </td>
      <td
        data-label="Что изменится"
        className={`table__block divergence__effect${item.warns ? ' divergence__effect--warn' : ''}`}
      >
        {item.effect}
      </td>
    </tr>
  )
}
